import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import supabase from '@/lib/supabase';

interface ReportRow {
  id: number;
  post_id: number;
  reporter_id: string;
  reason: string;
  detail: string | null;
  created_at: string;
  posts: {
    id: number;
    title: string;
    category: string;
    author: string;
    created_at: string;
  } | null;
}

export default function PostReportsSection() {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [actionError, setActionError] = useState('');

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('reports')
        .select('id, post_id, reporter_id, reason, detail, created_at, posts:post_id(id, title, category, author, created_at)')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setReports((data || []) as ReportRow[]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setError(message || '신고 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleDismissReport = async (reportId: number) => {
    if (!confirm('이 신고를 기각하시겠습니까? 신고 기록이 삭제됩니다.')) return;

    setActionLoading(reportId);
    setActionError('');
    try {
      const { error: deleteError } = await supabase
        .from('reports')
        .delete()
        .eq('id', reportId);

      if (deleteError) throw deleteError;

      setReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setActionError(message || '처리 중 오류가 발생했습니다.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeletePost = async (reportId: number, postId: number) => {
    if (!confirm('정말로 이 게시글을 삭제하시겠습니까? 삭제된 게시글은 복구할 수 없으며, 관련 신고도 함께 삭제됩니다.')) return;

    setActionLoading(reportId);
    setActionError('');
    try {
      const { error: postError } = await supabase
        .from('posts')
        .delete()
        .eq('id', postId);

      if (postError) throw postError;

      await supabase
        .from('reports')
        .delete()
        .eq('post_id', postId);

      setReports((prev) => prev.filter((r) => r.post_id !== postId));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setActionError(message || '처리 중 오류가 발생했습니다.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr: string): string => {
    try {
      const d = new Date(dateStr);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${y}.${m}.${day} ${h}:${min}`;
    } catch {
      return dateStr;
    }
  };

  const reasonBadgeColor = (reason: string) => {
    switch (reason) {
      case '스팸/광고': return 'bg-yellow-100 text-yellow-700';
      case '혐오 발언': return 'bg-red-100 text-red-700';
      case '개인정보 노출': return 'bg-orange-100 text-orange-700';
      case '허위 정보': return 'bg-purple-100 text-purple-700';
      default: return 'bg-secondary-100 text-secondary-700';
    }
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-bold text-foreground-950 mb-1">게시글 신고 관리</h2>
            <p className="text-sm text-foreground-500">사용자가 신고한 게시글을 검토하고 처리합니다</p>
          </div>
        </div>
        <div className="text-center py-20 bg-white rounded-lg border border-foreground-200">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-foreground-500">신고 목록을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-foreground-950 mb-1">게시글 신고 관리</h2>
          <p className="text-sm text-foreground-500">사용자가 신고한 게시글을 검토하고 처리합니다</p>
        </div>
        <button
          onClick={fetchReports}
          className="inline-flex items-center gap-2 bg-white border border-foreground-200 hover:bg-background-50 text-foreground-700 text-sm font-medium px-4 py-2 rounded-md transition-colors whitespace-nowrap"
        >
          <i className="ri-refresh-line" /> 새로고침
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {actionError && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500" />
          <p className="text-sm text-red-600">{actionError}</p>
        </div>
      )}

      {reports.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-lg border border-foreground-200">
          <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-check-line text-2xl text-green-500" />
          </div>
          <h3 className="text-base font-bold text-foreground-950 mb-1">신고된 게시글이 없습니다</h3>
          <p className="text-sm text-foreground-500">모든 신고가 처리되었습니다.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-foreground-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-foreground-200 bg-background-100">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">게시글</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">카테고리</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">작성자</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">신고 사유</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">상세 내용</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-600">신고일</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-foreground-600">처리</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => {
                  const post = report.posts;
                  const isBusy = actionLoading === report.id;

                  return (
                    <tr key={report.id} className="border-b border-foreground-200 last:border-b-0 hover:bg-background-50">
                      <td className="px-4 py-3">
                        {post ? (
                          <Link
                            to={`/board/${post.id}`}
                            className="text-sm font-medium text-primary-500 hover:text-primary-600 hover:underline"
                          >
                            {post.title}
                          </Link>
                        ) : (
                          <span className="text-sm text-foreground-400 line-through">삭제된 게시글</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {post ? (
                          <span className="text-xs text-foreground-600">{post.category}</span>
                        ) : (
                          <span className="text-xs text-foreground-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {post ? (
                          <span className="text-xs text-foreground-600">{post.author}</span>
                        ) : (
                          <span className="text-xs text-foreground-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${reasonBadgeColor(report.reason)}`}>
                          {report.reason}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-foreground-500 max-w-[200px] truncate block">
                          {report.detail || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-foreground-500 whitespace-nowrap">
                        {formatDate(report.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleDismissReport(report.id)}
                            disabled={isBusy}
                            className="text-xs text-foreground-500 hover:text-foreground-700 font-medium whitespace-nowrap disabled:opacity-50"
                          >
                            신고 기각
                          </button>
                          {post && (
                            <button
                              onClick={() => handleDeletePost(report.id, post.id)}
                              disabled={isBusy}
                              className="text-xs text-red-500 hover:text-red-600 font-medium whitespace-nowrap disabled:opacity-50"
                            >
                              게시글 삭제
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}